import { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API, useAuth } from "@/App";
import { Mic, Square, Camera, Video, Plus, Check, Clock, Loader2, X, Image as ImageIcon, Trash2, Pause, Play, Utensils } from "lucide-react";
import gustoAvatar from "@/assets/gusto-avatar.png";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { motion, AnimatePresence } from "framer-motion";

const RecordPage = () => {
  const navigate = useNavigate();
  const { token } = useAuth();
  
  // Recording state
  const [isRecording, setIsRecording] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const [pausedTime, setPausedTime] = useState(0); // Time when paused
  const [recipe, setRecipe] = useState(null);
  const [steps, setSteps] = useState([]);
  const [currentTranscription, setCurrentTranscription] = useState("");
  const [isProcessing, setIsProcessing] = useState(false);
  const [isGeneratingImage, setIsGeneratingImage] = useState(false);
  
  // Media state
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const timerRef = useRef(null);
  const startTimeRef = useRef(0);
  const photoInputRef = useRef(null);
  const videoInputRef = useRef(null);
  
  // Dialog states
  const [showStartDialog, setShowStartDialog] = useState(true);
  const [showFinishDialog, setShowFinishDialog] = useState(false);
  const [showMediaDialog, setShowMediaDialog] = useState(false);
  const [selectedStepForMedia, setSelectedStepForMedia] = useState(null);
  const [recipeName, setRecipeName] = useState("");
  const [recipeDescription, setRecipeDescription] = useState("");
  const [recipeServings, setRecipeServings] = useState(4);
  const [recipeCategory, setRecipeCategory] = useState("");
  const [recipeTags, setRecipeTags] = useState([]);
  const [currentStepType, setCurrentStepType] = useState("preparation");
  const [finalImage, setFinalImage] = useState(null);

  // Categories and tags
  const CATEGORIES = [
    { id: "entree", label: "Entrée", emoji: "🥗" },
    { id: "plat", label: "Plat", emoji: "🍽️" },
    { id: "dessert", label: "Dessert", emoji: "🍰" },
    { id: "boisson", label: "Boisson", emoji: "🥤" },
    { id: "autre", label: "Autre", emoji: "🍳" },
  ];

  const TAGS = [
    { id: "vege", label: "Végétarien", emoji: "🥬" },
    { id: "vegan", label: "Vegan", emoji: "🌱" },
    { id: "sans_gluten", label: "Sans gluten", emoji: "🌾" },
    { id: "sans_cuisson", label: "Sans cuisson", emoji: "❄️" },
    { id: "rapide", label: "Rapide (<30min)", emoji: "⚡" },
    { id: "facile", label: "Facile", emoji: "👶" },
    { id: "gastronomique", label: "Gastronomique", emoji: "⭐" },
  ];

  const toggleTag = (tagId) => {
    setRecipeTags(prev => 
      prev.includes(tagId) 
        ? prev.filter(t => t !== tagId)
        : [...prev, tagId]
    );
  };
  
  // Generate automatic recipe number
  const generateRecipeNumber = () => {
    const timestamp = Date.now();
    const num = String(timestamp).slice(-6).padStart(6, '0');
    return `Recette #${num}`;
  };

  // Aggregate ingredients from all steps (supports both old 'ingredient' and new 'ingredients' format)
  const aggregatedIngredients = steps.reduce((acc, step) => {
    // Handle new format (ingredients array)
    const stepIngredients = step.ingredients || (step.ingredient ? [step.ingredient] : []);
    
    stepIngredients.forEach(ing => {
      if (!ing || !ing.name) return;
      const existing = acc.find(i => i.name.toLowerCase() === ing.name.toLowerCase());
      if (existing) {
        if (ing.quantity && !existing.quantity.includes(ing.quantity)) {
          existing.quantity += existing.quantity ? ` + ${ing.quantity}` : ing.quantity;
        }
        // Keep the image if we have one
        if (ing.image_url && !existing.image_url) {
          existing.image_url = ing.image_url;
        }
      } else {
        acc.push({ ...ing });
      }
    });
    return acc;
  }, []);

  // Aggregate utensils from all steps (supports both old 'utensil' and new 'utensils' format)
  const usedUtensils = [...new Set(steps.flatMap(s => {
    const stepUtensils = s.utensils || (s.utensil ? [s.utensil] : []);
    return stepUtensils;
  }))];

  // Format time
  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  // Start recording timer - with pause support
  useEffect(() => {
    if (recipe && !showStartDialog && !isPaused) {
      timerRef.current = setInterval(() => {
        setRecordingTime(prev => prev + 1);
      }, 1000);
    } else {
      clearInterval(timerRef.current);
    }
    return () => clearInterval(timerRef.current);
  }, [recipe, showStartDialog, isPaused]);

  // Toggle pause
  const togglePause = () => {
    if (isPaused) {
      // Resuming - adjust start time
      const pauseDuration = Date.now() - pausedTime;
      startTimeRef.current += pauseDuration;
      setIsPaused(false);
      toast.success("Chrono relancé !");
    } else {
      // Pausing
      setPausedTime(Date.now());
      setIsPaused(true);
      toast.info("Chrono en pause");
    }
  };

  // Start recipe
  const startRecipe = async () => {
    const autoName = generateRecipeNumber();
    setRecipeName(autoName);
    
    try {
      const response = await axios.post(
        `${API}/recipes`,
        { name: autoName, servings: 4, description: null },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setRecipe(response.data);
      setShowStartDialog(false);
      startTimeRef.current = Date.now();
      toast.success("C'est parti ! Commencez à dicter vos étapes.");
    } catch (error) {
      console.error("Error creating recipe:", error);
      toast.error("Erreur lors de la création de la recette");
    }
  };

  // Start audio recording
  const startAudioRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      mediaRecorderRef.current = new MediaRecorder(stream, { mimeType: 'audio/webm' });
      audioChunksRef.current = [];
      
      mediaRecorderRef.current.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };
      
      mediaRecorderRef.current.onstop = async () => {
        const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
        await processAudio(audioBlob);
      };
      
      mediaRecorderRef.current.start();
      setIsRecording(true);
      toast.success("Enregistrement démarré - Dictez !");
    } catch (error) {
      console.error("Error starting recording:", error);
      toast.error("Impossible d'accéder au microphone");
    }
  };

  // Stop audio recording
  const stopAudioRecording = () => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      mediaRecorderRef.current.stop();
      mediaRecorderRef.current.stream.getTracks().forEach(track => track.stop());
      setIsRecording(false);
    }
  };

  // Process audio with Whisper
  const processAudio = async (audioBlob) => {
    setIsProcessing(true);
    try {
      const formData = new FormData();
      formData.append('audio', audioBlob, 'recording.webm');
      
      const response = await axios.post(`${API}/transcribe`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      const { text, ingredient, utensil, action, all_ingredients, all_utensils } = response.data;
      setCurrentTranscription(text);
      
      if (text.trim()) {
        const timestamp = (Date.now() - startTimeRef.current) / 1000;
        const newStep = {
          id: Date.now().toString(),
          timestamp,
          description: text,
          ingredients: all_ingredients || (ingredient ? [ingredient] : []),
          utensils: all_utensils || (utensil ? [utensil] : []),
          action: action || null,
          media_urls: [],
          photos: [],
          step_type: currentStepType
        };
        
        // Generate images for ALL ingredients
        if (newStep.ingredients.length > 0) {
          setIsGeneratingImage(true);
          for (let i = 0; i < newStep.ingredients.length; i++) {
            try {
              const imgFormData = new FormData();
              imgFormData.append('ingredient_name', newStep.ingredients[i].name);
              const imgResponse = await axios.post(`${API}/generate-ingredient-image`, imgFormData);
              newStep.ingredients[i].image_url = `data:image/png;base64,${imgResponse.data.image_base64}`;
            } catch (imgError) {
              console.error(`Error generating image for ${newStep.ingredients[i].name}:`, imgError);
            }
          }
          setIsGeneratingImage(false);
        }
        
        // Save step to backend
        await axios.post(
          `${API}/recipes/${recipe.id}/steps`,
          newStep,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        
        setSteps(prev => [...prev, newStep]);
        toast.success("Étape ajoutée !");
      }
    } catch (error) {
      console.error("Error processing audio:", error);
      toast.error("Erreur lors de la transcription");
    } finally {
      setIsProcessing(false);
    }
  };

  // Add manual step with parsing
  const addManualStep = async () => {
    if (!currentTranscription.trim()) return;
    
    setIsProcessing(true);
    try {
      const formData = new FormData();
      formData.append('text', currentTranscription);
      const parseResponse = await axios.post(`${API}/parse-text`, formData);
      const { ingredient, utensil, action, all_ingredients, all_utensils } = parseResponse.data;
      
      const timestamp = (Date.now() - startTimeRef.current) / 1000;
      const newStep = {
        id: Date.now().toString(),
        timestamp,
        description: currentTranscription,
        ingredients: all_ingredients || (ingredient ? [ingredient] : []),
        utensils: all_utensils || (utensil ? [utensil] : []),
        action: action || null,
        media_urls: [],
        photos: [],
        step_type: currentStepType
      };
      
      // Generate images for ALL ingredients
      if (newStep.ingredients.length > 0) {
        setIsGeneratingImage(true);
        for (let i = 0; i < newStep.ingredients.length; i++) {
          try {
            const imgFormData = new FormData();
            imgFormData.append('ingredient_name', newStep.ingredients[i].name);
            const imgResponse = await axios.post(`${API}/generate-ingredient-image`, imgFormData);
            newStep.ingredients[i].image_url = `data:image/png;base64,${imgResponse.data.image_base64}`;
          } catch (imgError) {
            console.error(`Error generating image for ${newStep.ingredients[i].name}:`, imgError);
          }
        }
        setIsGeneratingImage(false);
      }
      
      await axios.post(
        `${API}/recipes/${recipe.id}/steps`,
        newStep,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setSteps(prev => [...prev, newStep]);
      setCurrentTranscription("");
      toast.success("Étape ajoutée !");
    } catch (error) {
      console.error("Error adding step:", error);
      toast.error("Erreur lors de l'ajout");
    } finally {
      setIsProcessing(false);
    }
  };

  // Handle photo capture for a step
  const handlePhotoCapture = (stepId) => {
    setSelectedStepForMedia(stepId);
    photoInputRef.current?.click();
  };

  // Handle photo file selection
  const handlePhotoSelected = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !selectedStepForMedia) return;
    
    try {
      const reader = new FileReader();
      reader.onload = (event) => {
        const photoData = event.target.result;
        setSteps(prev => prev.map(step => {
          if (step.id === selectedStepForMedia) {
            return {
              ...step,
              photos: [...(step.photos || []), photoData]
            };
          }
          return step;
        }));
        toast.success("Photo ajoutée !");
      };
      reader.readAsDataURL(file);
    } catch (error) {
      console.error("Error adding photo:", error);
      toast.error("Erreur lors de l'ajout de la photo");
    }
    
    setSelectedStepForMedia(null);
    e.target.value = '';
  };

  // Handle final image capture
  const handleFinalImageCapture = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    const reader = new FileReader();
    reader.onload = (event) => {
      setFinalImage(event.target.result);
      toast.success("Photo de dressage ajoutée !");
    };
    reader.readAsDataURL(file);
    e.target.value = '';
  };

  // Delete step
  const deleteStep = (stepId) => {
    setSteps(prev => prev.filter(s => s.id !== stepId));
    toast.success("Étape supprimée");
  };

  // Finish recipe
  const finishRecipe = async () => {
    if (!recipeName.trim() || recipeName.startsWith("Recette #")) {
      toast.error("Donnez un nom à votre recette !");
      return;
    }
    
    if (!recipeCategory) {
      toast.error("Choisissez une catégorie !");
      return;
    }
    
    try {
      await axios.put(
        `${API}/recipes/${recipe.id}/finish`,
        {
          name: recipeName,
          servings: recipeServings,
          description: recipeDescription,
          final_image: finalImage,
          category: recipeCategory,
          tags: recipeTags
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success("Recette publiée !");
      navigate(`/recipe/${recipe.id}`);
    } catch (error) {
      console.error("Error finishing recipe:", error);
      toast.error("Erreur lors de la publication");
    }
  };

  // Cancel recipe
  const cancelRecipe = async () => {
    if (recipe) {
      try {
        await axios.delete(`${API}/recipes/${recipe.id}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
      } catch (error) {
        console.error("Error deleting recipe:", error);
      }
    }
    navigate("/");
  };

  return (
    <div className="px-4 py-6 max-w-4xl mx-auto" data-testid="record-page">
      {/* Hidden file inputs */}
      <input
        type="file"
        ref={photoInputRef}
        accept="image/*"
        capture="environment"
        onChange={handlePhotoSelected}
        className="hidden"
      />
      
      {/* Start Dialog */}
      <Dialog open={showStartDialog} onOpenChange={setShowStartDialog}>
        <DialogContent className="sm:max-w-md" data-testid="start-recipe-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl flex items-center gap-2">
              <img src={gustoAvatar} alt="" className="w-6 h-6" />
              Nouvelle Improvisation
            </DialogTitle>
            <DialogDescription>
              Lancez l'enregistrement et dictez vos étapes en cuisinant. Vous nommerez votre création à la fin !
            </DialogDescription>
          </DialogHeader>
          <div className="py-6 text-center">
            <div className="w-20 h-20 mx-auto rounded-full bg-primary/10 flex items-center justify-center mb-4">
              <Mic className="w-10 h-10 text-primary" />
            </div>
            <p className="text-sm text-muted-foreground">
              Appuyez sur le bouton micro pour dicter chaque étape.<br />
              Ex: "J'ajoute 30g de beurre"
            </p>
          </div>
          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button variant="outline" onClick={() => navigate("/")} className="rounded-full">
              Annuler
            </Button>
            <Button onClick={startRecipe} className="bg-primary hover:bg-primary/90 rounded-full" data-testid="start-recipe-btn">
              <Mic className="w-4 h-4 mr-2" />
              C'est parti !
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Finish Dialog */}
      <Dialog open={showFinishDialog} onOpenChange={setShowFinishDialog}>
        <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto" data-testid="finish-recipe-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl">Baptisez votre création !</DialogTitle>
            <DialogDescription>
              Donnez un nom et ajoutez une photo de dressage.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {/* Final Photo */}
            <div className="space-y-2">
              <Label>Photo de dressage</Label>
              <div className="relative">
                {finalImage ? (
                  <div className="relative aspect-video rounded-xl overflow-hidden bg-muted">
                    <img src={finalImage} alt="Dressage" className="w-full h-full object-cover" />
                    <Button
                      variant="destructive"
                      size="icon"
                      className="absolute top-2 right-2 rounded-full w-8 h-8"
                      onClick={() => setFinalImage(null)}
                    >
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                ) : (
                  <label className="flex flex-col items-center justify-center aspect-video rounded-xl border-2 border-dashed border-border hover:border-primary cursor-pointer transition-colors bg-muted/30">
                    <Camera className="w-10 h-10 text-muted-foreground mb-2" />
                    <span className="text-sm text-muted-foreground">Ajouter une photo</span>
                    <input
                      type="file"
                      accept="image/*"
                      capture="environment"
                      onChange={handleFinalImageCapture}
                      className="hidden"
                    />
                  </label>
                )}
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="finalName">Nom de la recette *</Label>
              <Input
                id="finalName"
                placeholder="Ex: Ma tarte aux pommes improvisée"
                value={recipeName.startsWith("Recette #") ? "" : recipeName}
                onChange={(e) => setRecipeName(e.target.value)}
                className="h-12 rounded-xl"
                data-testid="final-recipe-name-input"
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Nombre de parts</Label>
                <Select value={recipeServings.toString()} onValueChange={(v) => setRecipeServings(parseInt(v))}>
                  <SelectTrigger className="h-12 rounded-xl">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {[1, 2, 3, 4, 5, 6, 8, 10, 12].map(n => (
                      <SelectItem key={n} value={n.toString()}>{n} parts</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="p-4 bg-muted rounded-xl text-center">
                <p className="text-2xl font-bold text-primary">{formatTime(recordingTime)}</p>
                <p className="text-xs text-muted-foreground">Durée totale</p>
              </div>
            </div>

            <div className="space-y-2">
              <Label>Description (optionnel)</Label>
              <Textarea
                placeholder="Décrivez votre création..."
                value={recipeDescription}
                onChange={(e) => setRecipeDescription(e.target.value)}
                className="rounded-xl resize-none"
                rows={2}
              />
            </div>

            {/* Category Selection */}
            <div className="space-y-2">
              <Label>Catégorie *</Label>
              <div className="flex flex-wrap gap-2">
                {CATEGORIES.map((cat) => (
                  <Button
                    key={cat.id}
                    type="button"
                    variant={recipeCategory === cat.id ? "default" : "outline"}
                    size="sm"
                    onClick={() => setRecipeCategory(cat.id)}
                    className={`rounded-full ${recipeCategory === cat.id ? 'bg-primary' : ''}`}
                  >
                    <span className="mr-1">{cat.emoji}</span>
                    {cat.label}
                  </Button>
                ))}
              </div>
            </div>

            {/* Tags Selection */}
            <div className="space-y-2">
              <Label>Tags (optionnel)</Label>
              <div className="flex flex-wrap gap-2">
                {TAGS.map((tag) => (
                  <Badge
                    key={tag.id}
                    variant={recipeTags.includes(tag.id) ? "default" : "outline"}
                    className={`cursor-pointer ${
                      recipeTags.includes(tag.id) ? 'bg-accent text-white' : 'hover:bg-muted'
                    }`}
                    onClick={() => toggleTag(tag.id)}
                  >
                    <span className="mr-1">{tag.emoji}</span>
                    {tag.label}
                  </Badge>
                ))}
              </div>
            </div>

            {/* Summary */}
            <div className="grid grid-cols-2 gap-3">
              <div className="p-3 bg-primary/10 rounded-xl">
                <p className="text-lg font-bold text-primary">{steps.length}</p>
                <p className="text-xs text-muted-foreground">Étapes</p>
              </div>
              <div className="p-3 bg-secondary/10 rounded-xl">
                <p className="text-lg font-bold text-secondary">{aggregatedIngredients.length}</p>
                <p className="text-xs text-muted-foreground">Ingrédients</p>
              </div>
            </div>

            {aggregatedIngredients.length > 0 && (
              <div className="space-y-2">
                <Label>Ingrédients</Label>
                <div className="flex flex-wrap gap-2">
                  {aggregatedIngredients.map((ing, i) => (
                    <Badge key={i} variant="secondary" className="bg-secondary/20">
                      {ing.quantity} {ing.name}
                    </Badge>
                  ))}
                </div>
              </div>
            )}

            {usedUtensils.length > 0 && (
              <div className="space-y-2">
                <Label>Ustensiles utilisés</Label>
                <div className="flex flex-wrap gap-2">
                  {usedUtensils.map((ut, i) => (
                    <Badge key={i} variant="outline">
                      🍳 {ut}
                    </Badge>
                  ))}
                </div>
              </div>
            )}
          </div>
          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button variant="outline" onClick={() => setShowFinishDialog(false)} className="rounded-full">
              Continuer
            </Button>
            <Button 
              onClick={finishRecipe} 
              disabled={!recipeName.trim() || recipeName.startsWith("Recette #") || !recipeCategory}
              className="bg-secondary hover:bg-secondary/90 rounded-full"
              data-testid="publish-recipe-btn"
            >
              <Check className="w-4 h-4 mr-2" />
              Publier
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Recording Interface */}
      {recipe && (
        <>
          {/* Header with Timer and Pause */}
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="flex items-center gap-2">
                <h1 className="font-heading text-xl font-bold text-charcoal">{recipeName}</h1>
                <Badge variant="outline" className="text-xs bg-accent/20">Brouillon</Badge>
              </div>
              <div className="flex items-center gap-2 mt-1">
                <Badge 
                  variant="outline" 
                  className={`${isPaused ? 'bg-orange-100 text-orange-700 border-orange-200' : 'bg-primary/10 text-primary border-primary/20'}`}
                >
                  <Clock className="w-3 h-3 mr-1" />
                  {formatTime(recordingTime)}
                  {isPaused && " (pause)"}
                </Badge>
                <Badge variant="outline">{steps.length} étapes</Badge>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {/* Pause/Resume Button */}
              <Button 
                variant={isPaused ? "default" : "outline"}
                size="icon"
                onClick={togglePause}
                className={`rounded-full ${isPaused ? 'bg-orange-500 hover:bg-orange-600' : ''}`}
                data-testid="pause-btn"
              >
                {isPaused ? <Play className="w-4 h-4" /> : <Pause className="w-4 h-4" />}
              </Button>
              <Button 
                variant="ghost" 
                size="icon"
                onClick={cancelRecipe}
                className="text-muted-foreground hover:text-destructive"
              >
                <X className="w-5 h-5" />
              </Button>
            </div>
          </div>

          {/* Live Summary Panel - Ingredients & Utensils */}
          {(aggregatedIngredients.length > 0 || usedUtensils.length > 0) && (
            <Card className="mb-6 bg-gradient-to-r from-primary/5 to-secondary/5 border-primary/20">
              <CardContent className="p-4">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="font-heading font-semibold text-charcoal flex items-center gap-2">
                    <Utensils className="w-4 h-4 text-primary" />
                    Résumé en direct
                  </h3>
                  <div className="flex gap-2">
                    <Badge className="bg-primary/20 text-primary border-0">
                      {aggregatedIngredients.length} ingrédients
                    </Badge>
                    <Badge className="bg-secondary/20 text-secondary border-0">
                      {usedUtensils.length} ustensiles
                    </Badge>
                  </div>
                </div>
                
                {/* Ingredients */}
                {aggregatedIngredients.length > 0 && (
                  <div className="mb-3">
                    <p className="text-xs text-muted-foreground mb-2 font-medium">INGRÉDIENTS</p>
                    <div className="flex flex-wrap gap-2">
                      {aggregatedIngredients.map((ing, i) => (
                        <div 
                          key={i} 
                          className="flex items-center gap-2 bg-white rounded-full px-3 py-1.5 shadow-sm border border-primary/10"
                        >
                          {ing.image_url && (
                            <img src={ing.image_url} alt="" className="w-6 h-6 rounded-full object-cover" />
                          )}
                          <span className="text-sm font-medium text-charcoal">{ing.name}</span>
                          <span className="text-xs text-primary font-bold">{ing.quantity}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                
                {/* Utensils */}
                {usedUtensils.length > 0 && (
                  <div>
                    <p className="text-xs text-muted-foreground mb-2 font-medium">USTENSILES</p>
                    <div className="flex flex-wrap gap-2">
                      {usedUtensils.map((ut, i) => (
                        <div 
                          key={i} 
                          className="flex items-center gap-1 bg-white rounded-full px-3 py-1.5 shadow-sm border border-secondary/10"
                        >
                          <span className="text-sm">🍳</span>
                          <span className="text-sm font-medium text-charcoal capitalize">{ut}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* Step Type Selector */}
          <div className="flex gap-2 mb-6 overflow-x-auto">
            {["preparation", "cuisson", "dressage"].map((type) => (
              <Button
                key={type}
                variant={currentStepType === type ? "default" : "outline"}
                onClick={() => setCurrentStepType(type)}
                className={`rounded-full capitalize flex-shrink-0 ${currentStepType === type ? 'bg-primary' : ''}`}
              >
                {type}
              </Button>
            ))}
          </div>

          {/* Recording Button */}
          <div className="flex flex-col items-center mb-8">
            <motion.button
              whileTap={{ scale: 0.95 }}
              onClick={isRecording ? stopAudioRecording : startAudioRecording}
              disabled={isProcessing || isPaused}
              className={`w-24 h-24 rounded-full flex items-center justify-center shadow-2xl transition-colors ${
                isRecording 
                  ? 'bg-destructive animate-recording' 
                  : isPaused
                    ? 'bg-gray-400'
                    : 'bg-primary hover:bg-primary/90'
              } ${isProcessing ? 'opacity-50' : ''}`}
              data-testid="record-button"
            >
              {isProcessing ? (
                <Loader2 className="w-10 h-10 text-white animate-spin" />
              ) : isRecording ? (
                <Square className="w-10 h-10 text-white" />
              ) : (
                <Mic className="w-10 h-10 text-white" />
              )}
            </motion.button>
            <p className="text-sm text-muted-foreground mt-3 text-center">
              {isPaused
                ? "Chrono en pause - Appuyez sur ▶️ pour reprendre"
                : isProcessing 
                  ? "Traitement..." 
                  : isRecording 
                    ? "Appuyez pour arrêter" 
                    : "Appuyez pour dicter"}
            </p>
            {isGeneratingImage && (
              <p className="text-xs text-primary mt-1 flex items-center gap-1">
                <Loader2 className="w-3 h-3 animate-spin" />
                Génération de l'illustration...
              </p>
            )}
          </div>

          {/* Manual Input */}
          <Card className={`mb-6 ${isPaused ? 'opacity-50' : ''}`}>
            <CardContent className="p-4">
              <Label className="text-sm text-muted-foreground mb-2 block">
                {isPaused ? "⏸️ En pause - reprenez le chrono pour ajouter" : "Ou écrivez :"}
              </Label>
              <div className="flex gap-2">
                <Input
                  placeholder="Ex: J'ajoute 50g de beurre"
                  value={currentTranscription}
                  onChange={(e) => setCurrentTranscription(e.target.value)}
                  className="flex-1 rounded-xl"
                  disabled={isProcessing || isPaused}
                  onKeyDown={(e) => e.key === 'Enter' && !isProcessing && !isPaused && currentTranscription.trim() && addManualStep()}
                />
                <Button 
                  onClick={addManualStep} 
                  disabled={!currentTranscription.trim() || isProcessing || isPaused}
                  className="bg-secondary hover:bg-secondary/90 rounded-xl"
                >
                  {isProcessing ? <Loader2 className="w-5 h-5 animate-spin" /> : <Plus className="w-5 h-5" />}
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Steps List */}
          <div className="space-y-4 mb-24">
            <h2 className="font-heading text-lg font-semibold">Étapes ({steps.length})</h2>
            <AnimatePresence>
              {steps.length === 0 ? (
                <p className="text-muted-foreground text-center py-8">Aucune étape. Commencez à dicter !</p>
              ) : (
                <div className="space-y-4">
                  {steps.map((step, index) => (
                    <motion.div
                      key={step.id}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, x: -100 }}
                      className="relative"
                    >
                      <Card className="bg-white border-l-4 border-l-primary">
                        <CardContent className="p-4">
                          <div className="flex items-start gap-3">
                            {/* Step number */}
                            <div className="w-8 h-8 rounded-full bg-primary text-white flex items-center justify-center text-sm font-bold flex-shrink-0">
                              {index + 1}
                            </div>
                            
                            {/* Images of ingredients */}
                            {(() => {
                              const stepIngs = step.ingredients || (step.ingredient ? [step.ingredient] : []);
                              const imagesWithUrl = stepIngs.filter(i => i.image_url);
                              if (imagesWithUrl.length > 0) {
                                return (
                                  <div className="flex gap-1 flex-shrink-0">
                                    {imagesWithUrl.slice(0, 3).map((ing, imgIdx) => (
                                      <div key={imgIdx} className="w-12 h-12 rounded-xl overflow-hidden bg-muted">
                                        <img src={ing.image_url} alt={ing.name} className="w-full h-full object-cover" />
                                      </div>
                                    ))}
                                    {imagesWithUrl.length > 3 && (
                                      <div className="w-12 h-12 rounded-xl bg-muted flex items-center justify-center text-xs text-muted-foreground">
                                        +{imagesWithUrl.length - 3}
                                      </div>
                                    )}
                                  </div>
                                );
                              }
                              return null;
                            })()}
                            
                            <div className="flex-1 min-w-0">
                              <div className="flex flex-wrap items-center gap-1 mb-1">
                                <Badge variant="outline" className="text-xs">
                                  {formatTime(Math.floor(step.timestamp))}
                                </Badge>
                                <Badge 
                                  className={`text-xs ${
                                    step.step_type === 'cuisson' ? 'bg-orange-100 text-orange-700' :
                                    step.step_type === 'dressage' ? 'bg-purple-100 text-purple-700' :
                                    'bg-green-100 text-green-700'
                                  }`}
                                >
                                  {step.step_type}
                                </Badge>
                                {step.action && (
                                  <Badge className="text-xs bg-blue-100 text-blue-700">{step.action}</Badge>
                                )}
                              </div>
                              <p className="text-sm text-charcoal">{step.description}</p>
                              
                              {/* Detected elements - ALL ingredients and utensils - ALL ingredients and utensils */}
                              <div className="flex flex-wrap gap-1 mt-2">
                                {/* Support both old format (ingredient) and new format (ingredients) */}
                                {(step.ingredients || (step.ingredient ? [step.ingredient] : [])).map((ing, idx) => (
                                  <span key={idx} className="inline-flex items-center gap-1 text-xs bg-primary/10 text-primary px-2 py-0.5 rounded-full">
                                    {ing.image_url && (
                                      <img src={ing.image_url} alt="" className="w-4 h-4 rounded-full object-cover" />
                                    )}
                                    🥘 {ing.quantity} {ing.name}
                                  </span>
                                ))}
                                {/* Support both old format (utensil) and new format (utensils) */}
                                {(step.utensils || (step.utensil ? [step.utensil] : [])).map((ut, idx) => (
                                  <span key={idx} className="text-xs bg-secondary/10 text-secondary px-2 py-0.5 rounded-full">
                                    🍳 {ut}
                                  </span>
                                ))}
                              </div>

                              {/* Photos */}
                              {step.photos && step.photos.length > 0 && (
                                <div className="flex gap-2 mt-2 overflow-x-auto">
                                  {step.photos.map((photo, pi) => (
                                    <img 
                                      key={pi}
                                      src={photo}
                                      alt={`Photo ${pi + 1}`}
                                      className="w-16 h-16 rounded-lg object-cover flex-shrink-0"
                                    />
                                  ))}
                                </div>
                              )}
                            </div>

                            {/* Actions */}
                            <div className="flex flex-col gap-1">
                              <Button
                                variant="ghost"
                                size="icon"
                                className="w-8 h-8"
                                onClick={() => handlePhotoCapture(step.id)}
                              >
                                <Camera className="w-4 h-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="icon"
                                className="w-8 h-8 text-destructive"
                                onClick={() => deleteStep(step.id)}
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    </motion.div>
                  ))}
                </div>
              )}
            </AnimatePresence>
          </div>

          {/* Finish Button */}
          {steps.length > 0 && (
            <div className="fixed bottom-24 left-4 right-4 max-w-4xl mx-auto">
              <Button
                onClick={() => setShowFinishDialog(true)}
                className="w-full bg-secondary hover:bg-secondary/90 h-14 rounded-full shadow-lg text-lg font-semibold"
              >
                <Check className="w-5 h-5 mr-2" />
                Terminer la recette
              </Button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default RecordPage;
